// ============================================================================
// Wave 3.99n — Section tint tokens
//
// Purpose:
//   Provide a curated, eye-friendly palette used across every long detail
//   page (Order Detail, Goldsmith Detail, Vendor Detail, Jewellery Detail
//   and any future multi-section page) so that:
//
//   • Each section renders on a subtly-tinted mat that visually
//     distinguishes it from its neighbours.
//   • The corresponding chip in the ``StickySectionNav`` at the top of
//     the page carries the SAME colour cue (small dot when inactive,
//     tint fill when active) so operators can build muscle memory:
//     "purple chip ⇒ Comments section".
//
// Design rules (locked with user for Wave 3.99n):
//   1) Moderate intensity — clearly coloured but soft to the eyes.
//   2) Cool + jewel tones lean: violet / purple / magenta (fuchsia) /
//      sky / indigo / teal / pink, plus warm accents (amber / emerald /
//      rose / stone) to keep material-adjacent sections legible.
//   3) Every entry ships with a matching *border*, *left strip*, *dot*,
//      and *chip* class so consumers just pick a key and get a
//      cohesive look everywhere.
//
// Consumption pattern:
//   <section id="…" className={cn(sectionMat(tint))}>
//     <YourCard />
//   </section>
//
//   <StickySectionNav
//     sections={[{ id, label, tint: 'violet' /* etc */ }]}
//   />
// ============================================================================

/**
 * Very-subtle light backgrounds — user feedback: previous ``-100/60``
 * mats read as bright / vibrant, so Wave 3.99n hot-fix drops the mat
 * intensity to a ``-50`` shade at ``40-50%`` opacity, softens the
 * border to ``-100`` and the left-strip to ``-300``. The dot stays at
 * ``-400`` so operators can still locate the colour cue on chips.
 * ``chipActive`` keeps ``-100`` fill + ``-700`` text so the active
 * chip remains legible without dominating the bar.
 *
 * ``matClass`` is the *marker* class consumed by descendant CSS rules
 * in ``index.css`` — those rules re-tint the inner Card using
 * ``color-mix`` so the Card background sits between white and the
 * hue's ``-50`` shade, producing a very quiet tint.
 */
export const SECTION_TINTS = {
  violet: {
    matClass: 'section-tint-violet',
    mat: 'bg-violet-50/50',
    border: 'border-violet-100',
    strip: 'border-l-violet-300',
    dot: 'bg-violet-400',
    chipActive: 'bg-violet-100 text-violet-700 border-violet-200',
  },
  purple: {
    matClass: 'section-tint-purple',
    mat: 'bg-purple-50/50',
    border: 'border-purple-100',
    strip: 'border-l-purple-300',
    dot: 'bg-purple-400',
    chipActive: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  fuchsia: {
    matClass: 'section-tint-fuchsia',
    mat: 'bg-fuchsia-50/50',
    border: 'border-fuchsia-100',
    strip: 'border-l-fuchsia-300',
    dot: 'bg-fuchsia-400',
    chipActive: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  },
  pink: {
    matClass: 'section-tint-pink',
    mat: 'bg-pink-50/50',
    border: 'border-pink-100',
    strip: 'border-l-pink-300',
    dot: 'bg-pink-400',
    chipActive: 'bg-pink-100 text-pink-700 border-pink-200',
  },
  rose: {
    matClass: 'section-tint-rose',
    mat: 'bg-rose-50/50',
    border: 'border-rose-100',
    strip: 'border-l-rose-300',
    dot: 'bg-rose-400',
    chipActive: 'bg-rose-100 text-rose-700 border-rose-200',
  },
  sky: {
    matClass: 'section-tint-sky',
    mat: 'bg-sky-50/50',
    border: 'border-sky-100',
    strip: 'border-l-sky-300',
    dot: 'bg-sky-400',
    chipActive: 'bg-sky-100 text-sky-700 border-sky-200',
  },
  indigo: {
    matClass: 'section-tint-indigo',
    mat: 'bg-indigo-50/50',
    border: 'border-indigo-100',
    strip: 'border-l-indigo-300',
    dot: 'bg-indigo-400',
    chipActive: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  },
  teal: {
    matClass: 'section-tint-teal',
    mat: 'bg-teal-50/50',
    border: 'border-teal-100',
    strip: 'border-l-teal-300',
    dot: 'bg-teal-400',
    chipActive: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  emerald: {
    matClass: 'section-tint-emerald',
    mat: 'bg-emerald-50/50',
    border: 'border-emerald-100',
    strip: 'border-l-emerald-300',
    dot: 'bg-emerald-400',
    chipActive: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  amber: {
    matClass: 'section-tint-amber',
    mat: 'bg-amber-50/50',
    border: 'border-amber-100',
    strip: 'border-l-amber-300',
    dot: 'bg-amber-500',
    chipActive: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  stone: {
    matClass: 'section-tint-stone',
    mat: 'bg-stone-50/60',
    border: 'border-stone-100',
    strip: 'border-l-stone-300',
    dot: 'bg-stone-400',
    chipActive: 'bg-stone-100 text-stone-700 border-stone-200',
  },
  slate: {
    matClass: 'section-tint-slate',
    mat: 'bg-slate-50/60',
    border: 'border-slate-100',
    strip: 'border-l-slate-300',
    dot: 'bg-slate-400',
    chipActive: 'bg-slate-100 text-slate-700 border-slate-200',
  },
};

/**
 * Return the class string that wraps a section body in a coloured mat
 * with a left accent strip. Use it on the outer ``<section>`` (or any
 * container) that already holds a ``<Card>`` inside.
 *
 * Falls back to a neutral, essentially invisible frame if the tint key
 * is unknown, so consumers can't accidentally break the page with a
 * typo.
 */
export function sectionMat(tint) {
  const t = SECTION_TINTS[tint];
  if (!t) {
    return 'rounded-xl border border-transparent';
  }
  return [
    // Marker class consumed by descendant CSS rules in ``index.css``
    // that retint the inner Card to the matching hue.
    t.matClass,
    'rounded-xl border border-l-[4px]',
    t.mat,
    t.border,
    t.strip,
    'p-2',
  ].join(' ');
}

/**
 * Return the class fragments used by ``StickySectionNav`` for the
 * coloured dot (inactive chip) and the tint fill (active chip).
 */
export function sectionChipStyles(tint) {
  const t = SECTION_TINTS[tint];
  if (!t) return null;
  return {
    dot: t.dot,
    active: t.chipActive,
  };
}
